import apis from './apis';

/**
 * Legislation Scan API client.
 *
 * Wraps the backend /scans endpoints: trigger a scan of a document (matter or
 * standalone draft, uploaded file, or a review-session document) against
 * legislation and/or selected data sources, then poll status + findings. Live
 * findings additionally stream over PubSub (see useScanPubSub) on the group
 * equal to the returned scan_run_uuid.
 */

export type ScanSeverity = 'critical' | 'major' | 'minor' | 'info';

/**
 * A selected source. The frontend sends the research-catalogue shape
 * (`source_type` / `source_identifier`); the scan backend also accepts the
 * engine shape (`type` / `identifier`) and normalizes either. Kept loose so the
 * research `toSourceSelection()` output passes straight through.
 */
export interface ScanSelectedSource {
  source_type?: string;
  source_identifier?: string;
  source_name?: string;
  type?: string;
  identifier?: string;
  name?: string;
  search_index_name?: string;
}

export interface StartScanOptions {
  selected_sources?: ScanSelectedSource[];
  jurisdictions?: string[];
  language?: string | null;
}

export interface ScanStartResult {
  scan_run_uuid: string;
  status: string;
  pubsub?: { group: string; correlation_id: string };
  error?: string;
}

export interface ScanLegislation {
  cited?: any;
  title?: string | null;
  document_number?: string | null;
  external_url?: string | null;
  in_force?: boolean | null;
  authority_tier?: number | null;
}

export interface ScanFinding {
  uuid: string;
  source_document_uuid?: string | null;
  severity: ScanSeverity;
  finding_type?: string | null;
  flagged_text_span?: string | null;
  section_reference?: string | null;
  issue_description?: string | null;
  rationale?: string | null;
  /** A proposed corrected version of flagged_text_span, when the model offered one. */
  suggested_redline?: string | null;
  legislation?: ScanLegislation;
  // The live PubSub payload carries legislation under this key instead.
  legislation_cited?: any;
  jurisdiction?: string | null;
  risk_score?: number | null;
  confidence_score?: number | null;
  decision?: string | null;
}

export interface ScanRunStatus {
  uuid: string;
  source_document_uuid?: string | null;
  status: string;
  jurisdiction?: string | null;
  counts: { total: number; critical: number; major: number; minor: number; info: number };
  correlation_id?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  error?: string | null;
}

function unwrap<T>(data: any): T {
  // Trigger endpoints return the object directly; read endpoints wrap in
  // { success, data }.
  return (data && data.data !== undefined ? data.data : data) as T;
}

export async function startDraftScan(
  draftUuid: string, opts: StartScanOptions = {},
): Promise<ScanStartResult> {
  const res = await apis.post(`/scans/drafts/${draftUuid}`, opts);
  return res.data as ScanStartResult;
}

export async function startFileScan(
  fileUuid: string, opts: StartScanOptions = {},
): Promise<ScanStartResult> {
  const res = await apis.post(`/scans/files/${fileUuid}`, opts);
  return res.data as ScanStartResult;
}

export async function startReviewDocumentScan(
  sessionUuid: string, documentId: number, opts: StartScanOptions = {},
): Promise<ScanStartResult> {
  const res = await apis.post(`/scans/reviews/${sessionUuid}/documents/${documentId}`, opts);
  return res.data as ScanStartResult;
}

export async function getScanStatus(scanUuid: string): Promise<ScanRunStatus> {
  const res = await apis.get(`/scans/${scanUuid}`);
  return unwrap<ScanRunStatus>(res.data);
}

export async function getScanFindings(
  scanUuid: string,
  filters: { severity?: ScanSeverity; finding_type?: string } = {},
): Promise<{ scan: ScanRunStatus; findings: ScanFinding[] }> {
  const params = new URLSearchParams();
  if (filters.severity) params.set('severity', filters.severity);
  if (filters.finding_type) params.set('finding_type', filters.finding_type);
  const qs = params.toString();
  const res = await apis.get(`/scans/${scanUuid}/findings${qs ? `?${qs}` : ''}`);
  return unwrap<{ scan: ScanRunStatus; findings: ScanFinding[] }>(res.data);
}

/**
 * The most recent scan (status + findings) for a document, so a surface can
 * resurface a prior scan after a page refresh instead of starting fresh.
 * `sourceRef` is the draft/file uuid, or `<sessionUuid>#<documentId>` for a
 * review document. Returns `{ scan: null, findings: [] }` when never scanned.
 */
export async function getLatestScanForSource(
  sourceRef: string,
): Promise<{ scan: ScanRunStatus | null; findings: ScanFinding[] }> {
  const res = await apis.get(`/scans/latest?source=${encodeURIComponent(sourceRef)}`);
  return unwrap<{ scan: ScanRunStatus | null; findings: ScanFinding[] }>(res.data);
}

export interface ApplyFindingResult {
  applied: boolean;
  reason?: string;
  word_count?: number;
}

/**
 * Apply a finding's suggested redline to the scanned draft. The backend replaces
 * the flagged span with the suggestion and saves a NEW (restorable) version - it
 * never destructively overwrites. Returns applied=false when the flagged text is
 * no longer in the document (re-scan needed). Standalone drafts only.
 */
export async function applyScanFindingSuggestion(
  scanUuid: string, findingUuid: string,
): Promise<ApplyFindingResult> {
  const res = await apis.post(`/scans/${scanUuid}/findings/${findingUuid}/apply`, {});
  return (res.data && res.data.data !== undefined ? res.data.data : res.data) as ApplyFindingResult;
}
